import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ComboSelect.css';

/**
 * Shared styled single-select combobox — type-to-search dropdown that matches
 * the legacy ALPACA (DevExpress XAF) lookup control: an input with a trailing
 * chevron that opens a clean, contained, scrollable white list you filter by
 * typing.  Replaces the raw browser `<datalist>` (whose popup we can't style).
 *
 * The popup list is rendered via a PORTAL into `document.body`, positioned
 * with `position: fixed` from the control's own bounding rect — NOT nested
 * inside the control's DOM subtree. This is required so the popup is never
 * clipped by an ancestor's `overflow: auto/hidden` (e.g. AppDialog's
 * scrolling `.ad-body`) — without the portal, a ComboSelect used inside any
 * popup/scroll container renders an unusable, partially-hidden list.
 *
 * `allowCustom` keeps the free-text behaviour of an XAF lookup that also lets
 * you enter a value not in the list (e.g. a brand-new MAC module name).
 */
/** An option may be a bare string (value === label) or a {value,label} pair —
 *  the latter for id-keyed lookups (Position/Department/Division/etc.). */
export type ComboOption = string | { value: string; label: string };

interface ComboSelectProps {
  value: string;
  options: ComboOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  allowCustom?: boolean;
  disabled?: boolean;
  className?: string;
  /** Open the dropdown immediately on mount (inline cell editors). */
  autoOpen?: boolean;
}

export function ComboSelect({
  value, options, onChange, placeholder = 'Select…', allowCustom = false, disabled = false, className, autoOpen = false,
}: ComboSelectProps) {
  const [open, setOpen] = useState(autoOpen);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const norm = useMemo(
    () => options.map(o => typeof o === 'string' ? { value: o, label: o } : o),
    [options]);
  const selectedLabel = norm.find(o => o.value === value)?.label ?? (allowCustom ? value : '');

  // Position the portaled popup against the control, and keep it anchored
  // across window resize/scroll while open (the control itself may live
  // inside a scrolling dialog body).
  const reposition = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 2, left: r.left, width: r.width });
  };
  useLayoutEffect(() => { if (open) reposition(); }, [open]);
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  // Close on outside click — check both the control AND the portaled popup.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // When opening, focus the filter and seed it with the current value.
  useEffect(() => { if (open) { setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? norm.filter(o => o.label.toLowerCase().includes(q)) : norm;
  }, [norm, query]);

  const pick = (v: string) => { onChange(v); setOpen(false); };

  const commitCustom = () => {
    const q = query.trim();
    if (allowCustom && q) { onChange(q); setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`cmb${disabled ? ' cmb-disabled' : ''}${className ? ' ' + className : ''}`}>
      <button type="button" className="cmb-control" disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}>
        <span className={value ? 'cmb-val' : 'cmb-ph'}>{selectedLabel || placeholder}</span>
        {value && !disabled && (
          <i className="ti ti-x cmb-clear" title="Clear"
            onClick={(e) => { e.stopPropagation(); onChange(''); }} />
        )}
        <i className={`ti ti-chevron-down cmb-caret${open ? ' open' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          className="cmb-pop cmb-pop-portal"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width }}
        >
          <div className="cmb-search">
            <i className="ti ti-search" />
            <input ref={inputRef} value={query} placeholder="Type to filter…"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { const f0 = filtered[0]; if (filtered.length === 1 && f0) pick(f0.value); else commitCustom(); }
                else if (e.key === 'Escape') setOpen(false);
              }} />
          </div>
          <div className="cmb-list">
            {filtered.map(o => (
              <div key={o.value} className={`cmb-opt${o.value === value ? ' sel' : ''}`} onClick={() => pick(o.value)} title={o.label}>
                {o.label}
                {o.value === value && <i className="ti ti-check" />}
              </div>
            ))}
            {filtered.length === 0 && (
              allowCustom && query.trim()
                ? <div className="cmb-opt cmb-add" onClick={commitCustom}><i className="ti ti-plus" /> Use “{query.trim()}”</div>
                : <div className="cmb-empty">No matches</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
